import { Resources } from 'constants/resources';

import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import CreateIcon from '@mui/icons-material/Create';
import { blue } from '@mui/material/colors';
import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { useRecordModalById } from 'hooks/useRecordModalById';
import { GatewayRuleCardCollapseContent, GatewayRuleMapping } from 'pages/GatewayRules/components';
import { GatewayRuleField } from 'pages/GatewayRules/components/GatewayRuleField';
import { GatewayRuleInfo } from 'pages/GatewayRules/components/GatewayRuleInfo';
import { GatewayRuleMappingPercentEditDialog } from 'pages/GatewayRules/components/GatewayRuleMappingPercentEditDialog/GatewayRuleMappingPercentEditDialog';
import { GatewayRuleModalDelete } from 'pages/GatewayRules/components/GatewayRuleModalDelete/GatewayRuleModalDelete';
import { useExpandedCard, useGatewayRuleCardStyles } from 'pages/GatewayRules/hooks';
import { useEnableAdditionalFields } from 'pages/GatewayRules/hooks/useEnableAdditionalFields';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { RecordContextProvider, useRecordContext } from 'react-admin';
import { Box } from 'shared/mui/Box';
import { Collapse } from 'shared/mui/Collapse/Collapse';
import {
  ContextMenu,
  ContextMenuDeleteButton,
  ContextMenuEditButton,
  ContextMenuEntityUserActionsButton,
  ContextMenuShowButton,
} from 'shared/mui/ContextMenu';
import { Divider } from 'shared/mui/Divider/Divider';
import { IconButton } from 'shared/mui/IconButtons';
import { Typography } from 'shared/mui/Typography';
import { FunctionField, TextField } from 'shared/react-admin/Fields';
import { GatewayRuleActiveSwitch } from 'shared/widgets/GatewayRuleActiveSwitch/GatewayRuleActiveSwitch';
import { GatewayShowDialog } from 'shared/widgets/GatewayShowDialog/GatewayShowDialog';
import { Gateway } from 'types/embedded/gateway';
import { GatewayRule, Mapping, MappingItem } from 'types/embedded/gatewayRule';
import { Permission } from 'types/embedded/permission';

import {
  GatewayRuleCardHeaderActionsStyled,
  GatewayRuleCardHeaderContentStyled,
  GatewayRuleCardHeaderStyled,
  GatewayRuleCardStyled,
} from './GatewayRuleCard.styled';
import { GatewayRuleCardTitle } from './GatewayRuleCardTitle';

export function GatewayRuleCard() {
  const translate = useTypedTranslate();
  const { hasResourcePermissions, hasResourceAccess } = usePermissions();
  const { gatewayRuleCardSx } = useGatewayRuleCardStyles();

  const record = useRecordContext<GatewayRule>();

  const { isExpandedCard, toggleExpanded } = useExpandedCard(record?.id);

  const { enableAdditionalFields } = useEnableAdditionalFields(record?.merchant?.['@id']);

  const mappings = [...(record?.mappings ?? [])]
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((mapping) => ({ ...mapping, gatewayRule: { name: record?.name } }));
  const mappingPercentModal = useRecordModal<Mapping>();
  const deleteModal = useRecordModal();
  const gatewayModal = useRecordModalById<Gateway>({ resource: Resources.Gateways });

  function renderMappingFields(): JSX.Element | JSX.Element[] {
    return mappings.map((mapping) => {
      return (
        <RecordContextProvider key={mapping.id} value={mapping}>
          <GatewayRuleMapping enableAdditionalFields={enableAdditionalFields} key={mapping.id}>
            {hasResourceAccess(Resources.Gateways) && (
              <FunctionField
                label={translate('app.table.gateway')}
                render={(item: MappingItem) =>
                  item.gateway && (
                    <Typography
                      onClick={() => gatewayModal.handleOpenRecordModal(item.gateway?.['@id'])}
                      sx={{
                        cursor: 'pointer',
                        color: blue[700],
                        textDecoration: 'underline',
                        fontSize: '14px',
                      }}
                    >
                      {item.gateway.name}
                    </Typography>
                  )
                }
              />
            )}
            <TextField
              label={
                <Box alignItems="center" display="flex" gap={1}>
                  <Typography fontSize={14} fontWeight={500}>
                    {translate('app.gatewayRules.trafficPercentage')}
                  </Typography>
                  <IconButton onClick={() => mappingPercentModal.handleOpenRecordModal(mapping)}>
                    <CreateIcon />
                  </IconButton>
                </Box>
              }
              sortable={false}
              source="value"
            />
          </GatewayRuleMapping>
        </RecordContextProvider>
      );
    });
  }

  function renderArrow(): JSX.Element {
    return (
      <IconButton onClick={toggleExpanded} sx={gatewayRuleCardSx.button}>
        {isExpandedCard ? <ArrowUpward color="primary" /> : <ArrowDownward color="primary" />}
      </IconButton>
    );
  }

  return (
    <GatewayRuleCardStyled>
      <GatewayRuleCardHeaderStyled>
        {hasResourcePermissions(Resources.GatewayRules, [Permission.Full, Permission.Update]) && (
          <GatewayRuleActiveSwitch sx={{ marginRight: '16px', flexShrink: 0 }} />
        )}
        <GatewayRuleCardHeaderContentStyled>
          <GatewayRuleInfo />
          <GatewayRuleCardHeaderActionsStyled>
            {renderArrow()}
            <Divider flexItem orientation="vertical" sx={{ marginX: 2 }} />
            <ContextMenu
              render={() => [
                <ContextMenuShowButton key="show-button" />,
                hasResourcePermissions(Resources.GatewayRules, [
                  Permission.Full,
                  Permission.Update,
                ]) && <ContextMenuEditButton key="edit-button" />,
                hasResourceAccess(Resources.UserActions) && (
                  <ContextMenuEntityUserActionsButton
                    entityName="gateway_rule"
                    key="user-actions-button"
                  />
                ),
                hasResourcePermissions(Resources.GatewayRules, [
                  Permission.Full,
                  Permission.Delete,
                ]) && (
                  <ContextMenuDeleteButton onClick={() => deleteModal.handleOpenRecordModal()} />
                ),
              ]}
            />
          </GatewayRuleCardHeaderActionsStyled>
        </GatewayRuleCardHeaderContentStyled>
      </GatewayRuleCardHeaderStyled>
      <Collapse in={isExpandedCard} timeout="auto" unmountOnExit>
        <GatewayRuleCardCollapseContent>
          <Box
            sx={{
              borderRight: '1px solid rgba(0, 0, 0, 0.08)',
              padding: '16px',
              boxSizing: 'border-box',
            }}
          >
            <Box>
              <GatewayRuleCardTitle
                componentsProps={{ tooltip: { sx: { maxWidth: '236px' } } }}
                title={translate('app.gatewayRules.condition')}
                tooltipText={translate('app.gatewayRules.conditionTooltipText')}
              />
              <GatewayRuleField label={translate('app.gatewayRules.rules')} sortable={false} />
            </Box>
          </Box>
          <Box sx={{ boxSizing: 'border-box', padding: '16px' }}>
            <Box>
              <GatewayRuleCardTitle
                componentsProps={{ tooltip: { sx: { maxWidth: '358px' } } }}
                title={translate('app.gatewayRules.mappings')}
                tooltipText={translate('app.gatewayRules.mappingTooltipText')}
              />
              {renderMappingFields()}
            </Box>
          </Box>
        </GatewayRuleCardCollapseContent>
        {mappingPercentModal.record && (
          <GatewayRuleMappingPercentEditDialog
            mapping={mappingPercentModal.record}
            onClose={mappingPercentModal.handleCloseRecordModal}
            open={mappingPercentModal.openRecordModal}
          />
        )}
      </Collapse>
      {deleteModal.openRecordModal && (
        <GatewayRuleModalDelete
          id={record?.id}
          merchantName={record?.merchant?.name}
          onClose={deleteModal.handleCloseRecordModal}
          open={deleteModal.openRecordModal}
        />
      )}
      {gatewayModal.openRecordModal && (
        <GatewayShowDialog
          gateway={gatewayModal.record}
          isFetching={gatewayModal.isFetching}
          onClose={gatewayModal.handleCloseRecordModal}
          open={gatewayModal.openRecordModal}
        />
      )}
    </GatewayRuleCardStyled>
  );
}
